import { FC, useEffect, useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';

import { LoadingIndicator } from '@cfa/react-components';

import { extractGameUrl } from '@/utils/document-utils';

import { Brightcove, Game, Tridion, Xyleme } from '@/components/document';

import { SearchDocument } from '@/_generated/api-clients/xp';
import {
  documentFromDashboardOrResourceAtom,
  documentPersistedAtom,
} from '@/atoms/document-persisted';
/** View for all document types. */
const Document: FC = () => {
  const { icon, routedFrom } = useAtomValue(documentPersistedAtom);
  const document = useAtomValue(documentFromDashboardOrResourceAtom);

  const isGameDoc = document?.sourceSystem === 'GAME';
  const [fixedGameDoc, setFixedGameDoc] = useState<SearchDocument | null>(null);

  const [prevDocumentUrl, setPrevDocumentUrl] = useState(document?.contentUrl);
  if (prevDocumentUrl !== document?.contentUrl) {
    setPrevDocumentUrl(document?.contentUrl);
    setFixedGameDoc(null);
  }

  useEffect(() => {
    if (!document?.contentUrl || document.sourceSystem !== 'GAME') return;
    const contentUrl = document.contentUrl;
    const processGameDoc = async () => {
      const gameUrl = document.searchId
        ? contentUrl
        : await extractGameUrl(contentUrl);
      setFixedGameDoc({ ...document, contentUrl: gameUrl ?? contentUrl });
    };
    processGameDoc();
  }, [document]);

  const gameIsLoading = isGameDoc && !fixedGameDoc;

  const documentComp: React.ReactNode = useMemo(() => {
    const fixedDoc = isGameDoc && fixedGameDoc ? fixedGameDoc : null;
    switch (document?.sourceSystem) {
      case 'XYLEME':
        return <Xyleme document={document} />;
      case 'TRIDION':
        return (
          <Tridion document={document} icon={icon} routedFrom={routedFrom} />
        );
      case 'BRIGHTCOVE':
        return <Brightcove document={document} />;
      case 'GAME':
        return gameIsLoading || !fixedDoc ? null : <Game document={fixedDoc} />;
      default:
        return null;
    }
  }, [document, icon, routedFrom, isGameDoc, fixedGameDoc, gameIsLoading]);

  return gameIsLoading ? (
    <LoadingIndicator size="md" variant="page" />
  ) : (
    documentComp
  );
};

export default Document;
